/**
 * Team Inbox — ownership / claim / system-line PURE helpers (Phase 2).
 *
 * The list transport (`waListConversations`) predates V2 and does not
 * surface ownership, so the module polls `teamInboxAdmin · meta` alongside
 * it and MERGES the two here. Everything in this file is SDK-free and
 * clock-injected for the Node test runner
 * (test/teaminboxOwnership.test.cjs).
 */
import type {
  ConvMeta,
  InboxConversation,
  InboxMessage,
  InboxMeta,
  ThreadEvent,
} from './types';

/** Soft claim-lock TTL — MUST match functions/lib/teaminbox.CLAIM_TTL_MS. */
export const CLAIM_TTL_MS = 90_000;

/** How often a focused composer refreshes its claim (< TTL, with margin). */
export const CLAIM_REFRESH_MS = 60_000;

/* ───────────────────── meta merge ───────────────────── */

/**
 * PURE — overlay one ConvMeta onto a mapped conversation row. The meta is
 * authoritative for ownership/claim/escalation:
 *   meta.ownerUid undefined → engine has not evaluated the row → owner
 *                             stays as mapped (tolerate-absent, no chip)
 *   meta.ownerUid null      → red "Unassigned"
 *   meta.ownerUid set       → named chip
 * A claim by MYSELF is not a banner (I am the one typing) — claims are
 * kept raw here; claimBannerFor() applies the not-me + TTL rules.
 */
export function applyMeta(
  c: InboxConversation,
  meta: ConvMeta | undefined,
  nowMs: number,
): InboxConversation {
  if (!meta) return c;
  const next: InboxConversation = { ...c };
  if (meta.ownerUid !== undefined) {
    next.owner = meta.ownerUid
      ? { uid: meta.ownerUid, name: meta.ownerName || meta.ownerUid }
      : null;
  }
  next.allocReason = meta.allocReason ?? null;
  next.escalatedTier =
    typeof meta.escalatedTier === 'number' ? meta.escalatedTier : 0;
  const at = typeof meta.claimedAtMs === 'number' ? meta.claimedAtMs : null;
  const live =
    !!meta.claimedBy && at != null && nowMs - at >= 0 && nowMs - at < CLAIM_TTL_MS;
  next.claim = live
    ? {
        by: meta.claimedBy as string,
        name: meta.claimedByName || (meta.claimedBy as string),
        atMs: at as number,
      }
    : null;
  return next;
}

/** PURE — overlay a whole meta payload onto the mapped row list. */
export function mergeMeta(
  rows: InboxConversation[],
  meta: InboxMeta | null,
  nowMs: number,
): InboxConversation[] {
  if (!meta || !meta.rows) return rows;
  return rows.map((c) => applyMeta(c, meta.rows[c.key], nowMs));
}

/* ───────────────────── claim banner (R-C2) ───────────────────── */

/**
 * PURE — the "Khilendra is replying…" banner text for a conversation, or
 * null. Only ANOTHER teammate's live claim shows (my own claim is just me
 * typing).
 */
export function claimBannerFor(
  c: InboxConversation | null,
  myId: string | null,
  nowMs: number,
): string | null {
  if (!c || !c.claim) return null;
  if (myId && c.claim.by === myId) return null;
  if (nowMs - c.claim.atMs >= CLAIM_TTL_MS) return null;
  return `${c.claim.name} is replying…`;
}

/* ───────────────────── identity ("You") ───────────────────── */

/**
 * PURE — is this owner me? The engine writes staff-doc ids (E001…); the
 * auth surface exposes BOTH staffId and the Firebase uid, so both match.
 */
export function isMine(
  owner: InboxConversation['owner'],
  myIds: Array<string | null | undefined>,
): boolean {
  if (!owner) return false;
  return myIds.some((id) => !!id && owner.uid === id);
}

/* ───────────────────── system lines in the thread ───────────────────── */

/**
 * PURE — convert §7.3 allocation/escalation events into renderable
 * timeline entries. They become `kind:'system'` pseudo-messages so the
 * existing groupByDay date-pill machinery positions them; direction is
 * 'out' ON PURPOSE — the unread divider counts only direction 'in' and a
 * system line must never shift it.
 */
export function eventsToMessages(events: ThreadEvent[]): InboxMessage[] {
  return (events || [])
    .filter((e) => e && e.text)
    .map((e) => ({
      id: `ev_${e.id}`,
      direction: 'out' as const,
      who: 'human' as const,
      kind: 'system' as const,
      body: e.text,
      at: typeof e.ms === 'number' ? e.ms : null,
      deliveryState: null,
      attachment: null,
      transcript: null,
      templateName: null,
      raw: {} as InboxMessage['raw'],
    }));
}

/**
 * PURE — merge real messages + system lines into ONE ascending timeline.
 * Stable on ties (message before event at the same ms — the event usually
 * REACTS to the message). Entries without a timestamp keep their relative
 * order at the end, matching groupByDay's no-timestamp handling.
 */
export function mergeTimeline(
  messages: InboxMessage[],
  eventMessages: InboxMessage[],
): InboxMessage[] {
  const stamped: Array<{ m: InboxMessage; at: number; tie: number; seq: number }> = [];
  const unstamped: InboxMessage[] = [];
  let seq = 0;
  for (const m of messages) {
    if (typeof m.at === 'number') stamped.push({ m, at: m.at, tie: 0, seq: seq++ });
    else unstamped.push(m);
  }
  for (const m of eventMessages) {
    if (typeof m.at === 'number') stamped.push({ m, at: m.at, tie: 1, seq: seq++ });
    else unstamped.push(m);
  }
  stamped.sort((a, b) => a.at - b.at || a.tie - b.tie || a.seq - b.seq);
  return [...stamped.map((s) => s.m), ...unstamped];
}

/* ───────────────────── capability check (client mirror) ───────────────────── */

/**
 * PURE — does the meta `me.capabilities` list grant a key? Client-side
 * GATING ONLY (the server re-checks every action) — used to hide the S6
 * gear / Assign menu from people who would only get a permission error.
 */
export function can(meta: InboxMeta | null, key: string): boolean {
  return !!meta && Array.isArray(meta.me?.capabilities)
    ? meta.me.capabilities.includes(key)
    : false;
}
